import { CountryID } from "@al/base-lib";

/**
 * 在售状态
 */
export enum OnlineStatus {
  /**
   * 在售
   */
  InStock = 2,
  /**
   * 下架
   */
  GetOff = 5,
}

/**
 * 是否禁售
 */
export enum IsDisable {
  /**
   * 0-正常;1-禁售
   */
  Normal,
  /**
   * 0-正常;1-禁售
   */
  Disable,
}

/**
 * 是否售罄
 */
export enum IsSoldOut {
  /**
   * 0 有货
   */
  No,
  /**
   * 1 无货
   */
  Yes,
}

export type SpecificationInfoType = {
  /**
   * 属性名称
   */
  name: string;
  /**
   * 属性描述
   */
  desc: string;
};

export type SpuSaleInfoType = {
  /**
   * 属性名
   */
  name: string;
  /**
   * 属性值列举
   */
  values: string[];
};

export type SkuInfoType = {
  /**
   * skuId
   */
  skuId: string;
  /**
   * 第三方商品skuId
   */
  apiSkuId: string;
  /**
   * sku图缩略图
   */
  image: string;
  /**
   * 属性信息json
   */
  properties: Record<string, any>;
  /**
   * 原价
   */
  originalPrice: number;
  /**
   * 原价-税前
   */
  originalPriceNoVat: number;
  /**
   * 券后价
   */
  couponPrice: number;
  /**
   * 货币id
   */
  currencyId: number;
  /**
   * 售价
   */
  sellingPrice: number;
  /**
   * tag类型
   */
  tags: {
    /**
     * 标签类型,0-COD,1-其他
     */
    tagType: number;
  };
  /**
   * 货品id
   */
  goodsIds: undefined;
  freightInfo: {
    /**
     * 长
     */
    length: number;
    /**
     * 宽
     */
    width: number;
    /**
     * 高
     */
    height: number;
    /**
     * 重
     */
    weight: number;
  };
};

export interface CategoryInfoType {
  /**
   * 类目id
   */
  id: string;
  /**
   * 类目层级
   */
  level: number;
  /**
   * 类目名称
   */
  name: string;
}

export interface ProductDetailType {
  /**
   * id
   */
  spuId: string;
  /**
   * 状态(2:在售;5:下架)
   */
  status: OnlineStatus;
  /**
   * 0-正常;1-禁售
   */
  disabled: IsDisable;
  /**
   * 国家id
   */
  countryId: CountryID;
  /**
   * 是否支持预售
   */
  presellSupport: PresellSupportType;
  brandInfo: {
    /**
     * 品牌id
     */
    id: number;
    /**
     * 品牌名
     */
    name: string;
    /**
     * 品牌国际化通用名
     */
    nameGlobal: string;
    /**
     * 品牌logo
     */
    logo: string;
  };
  /**
   * 运费模板id
   */
  freightTemplateId: number;
  /**
   * 产品名称
   */
  name: string;
  /**
   * 主图
   */
  indexImage: string;
  /**
   * 售罄状态【0 有货  1 无货】
   */
  soldouted: IsSoldOut;
  /**
   * banner图
   */
  bannerImages: string[];
  /**
   * 属性集合
   */
  specificationInfos: SpecificationInfoType[];
  /**
   * sku
   */
  skuInfos: SkuInfoType[];
  /**
   * spu基础属性
   */
  spuBaseInfos: {
    /**
     * 属性名
     */
    name: string;
    /**
     * 属性值
     */
    value: undefined;
  }[];
  /**
   * spu销售属性
   */
  spuSaleInfos: SpuSaleInfoType[];
  /**
   * 商家id
   */
  vendorId: number;
  /**
   * 店铺id
   */
  shopId: number;
  /**
   * 售卖类型（1：自营(采销)、2：POP(寄售)、3：API_JD商品(代售)、4：API_BL商品(代售)）
   */
  sellMode: number;
  /**
   * 履约类型【1通过ERP履约（印尼自营）,2商家自行履约（不经过ERP，POP商家）,5履约前通知ERP由 商家自行履约（菲律宾自营、越南自营）】
   */
  fulfillmentType: number;
  /**
   * 类目信息列表
   */
  categoryInfos: CategoryInfoType[];
  /**
   * vat税率，0-1之间
   */
  vat: number;
}

export type AddressItemType = {
  /**
   * 地址id
   */
  conAddressId: string;
  /**
   * 收货人名称
   */
  conName: string;
  /**
   * 收货人联系电话
   */
  conPhone: string;
  /**
   * 邮箱,适用于法国,加密存储
   */
  email: string;
  /**
   * 邮编
   */
  postcode: string;
  /**
   * 国家id
   */
  countryId: number;
  /**
   * 省id
   */
  provinceId: number;
  /**
   * 省
   */
  provinceName: string;
  /**
   * 市id
   */
  cityId: number;
  /**
   * 市
   */
  cityName: string;
  /**
   * 区id
   */
  districtId: number;
  /**
   * 区
   */
  districtName: string;
  /**
   * 乡id
   */
  villageId: number;
  /**
   * 乡
   */
  villageName: string;
  /**
   * 详细地址
   */
  detailAddress: string;
  /**
   * 是否默认 1.默认  0.非默认
   */
  isDefault: number;
  /**
   * 地址有效标识 1 有效地址  0 无效地址
   */
  effectiveFlag: number;
  /**
   * 地址数据缺失状态（effectiveFlag的补充）  0 普通级别  1 重要缺失
   */
  dataStatus: number;
  extAddress: {
    /**
     * 备用地址，欧洲站才可能有
     */
    alternateAddress: string;
  };
  /**
   * 地址是否置灰标志 0 显示 1 置灰
   */
  isHidden: IsHiddenAddress;
};

// 地址是否置灰标志 0 显示 1 置灰
export enum IsHiddenAddress {
  /**
   * 显示
   */
  No,
  /**
   * 置灰
   */
  Yes,
}

export enum ActivityType {
  /**
   * 直降
   */
  Limited = 1,
  /**
   * 秒杀
   */
  FlashSale,
}

export type PromotionInfoType = {
  /**
   * skuId
   */
  skuId: number;
  /**
   * 活动id
   */
  activityId: number;
  /**
   * 活动类型 1-直降 2-秒杀
   */
  activityType: ActivityType;
  /**
   * 划线价
   */
  crossedPrice: number;
  /**
   * 商品活动价
   */
  activityPrice: number;
  /**
   * 活动剩余库存
   */
  stock: number;
  /**
   * 活动开始时间
   */
  beginTime: number;
  /**
   * 活动结束时间
   */
  endTime: number;
  /**
   * 折扣
   */
  discount: string;
  /**
   * 商详页引导样式 1-样式1 2--样式2 3-自定义 4-无
   */
  guideBarStyle: PromotionGuideBarType;
  /**
   * 引导图片
   */
  guideBarImage: string;
  /**
   * 左上角氛围图片
   */
  topLeftAtmosIcon: string;
  /**
   * 促销限购数量,0为不限制
   */
  limit: number;
  /**
   * 折上折标签名称
   */
  tagName: string;
};

// 商详页引导样式 1-样式1 2--样式2 3-自定义 4-无
export enum PromotionGuideBarType {
  /**
   * 1-样式1
   */
  StyleOne = 1,
  /**
   * 2--样式2
   */
  StyleTow,
  /**
   * 3-自定义
   */
  CustomStyle,
  /**
   * 4-无
   */
  None,
}

export type SaleStockInfoType = {
  /**
   * skuId
   */
  skuId: string;
  /**
   * 库存数量
   */
  quantity: number;
  /**
   * 状态1-现货，2-预售,默认1
   */
  sellStatus: SellStatusType;
};

export type SkuPriceType = {
  /**
   * skuId，前端指定或后台计算的最优skuId
   */
  skuId: number;
  /**
   * 商品划线价
   */
  crossedPrice: number;
  /**
   * 商品售价
   */
  sellingPrice: number;
  promotion: PromotionInfoType;
  nextPromotion: PromotionInfoType;
  /**
   * 所有库存信息，(按需返回，若参数指定，则会返回所有sku信息，为null时则前端默认有库存)
   */
  saleStockInfos: SaleStockInfoType[];
  /**
   * 商品券后价
   */
  couponPrice: number;
  /**
   * 是否使用了分期电商券（券仅限分期使用）
   */
  useInstallmentCoupon: boolean;
  /**
   * 可用的奖励金
   */
  availableReward: number;
  /**
   * 店铺券抵扣金额
   */
  shopCouponDiscount: number;
  /**
   * 平台券抵扣金额
   */
  platformCouponDiscount: number;
  /**
   * 未领取的优惠券模板id集合
   */
  unReceivedCouponTemplateIds: number[];
  /**
   * 分期计划信息（按需返回）
   */
  installmentPlanItems: InstallmentPlanItemType[];
  /**
   * 是否展示领券购买
   */
  getCouponSettlement: boolean;
};

export type InstallmentPlanItemType = {
  /**
   * 还款期数
   */
  periods: number;
  /**
   * 总还款额
   */
  totalRepay: number;
  /**
   * 总利息
   */
  totalInterest: number;
  /**
   * 首付款
   */
  downPayment: number;
  /**
   * 首付比例
   */
  downPaymentRate: number;
  /**
   * 月本金
   */
  principal: number;
  /**
   * 月还款额
   */
  monthlyPay: number;
  /**
   * 月利息
   */
  monthlyInterest: number;
  /**
   * 金融分期支付类型，0:全款，1:Pay In3，2:Paylater，3:普通分期。默认值3，兼容印尼环境 参考枚举：OrderPayTypeEnum.class
   */
  orderPayType: OrderPayTypeEnum;
  /**
   * payLater的还款日，时间戳
   */
  payLaterDay: number;
  /**
   * 利息券总抵扣额
   */
  interestCouponDiscount: number;
  /**
   * 未领取的优惠券模板id集合
   */
  unReceivedCouponIds: undefined;
};

// 0:全款，1:Pay In3，2:Paylater，3:普通分期。默认值3
export enum OrderPayTypeEnum {
  /**
   * 0:全款
   */
  FullPay = 0,
  /**
   * 1:Pay In3
   */
  PayIn3 = 1,
  /**
   * 2:Paylater
   */
  PayLater = 2,
  /**
   * 3:普通分期
   */
  NormalInstallment = 3,
}

export type FreightInfoType = {
  /**
   * 运费
   */
  freight: number;
  /**
   * 运费促销活动结算结果dto
   */
  freightShoppingCarSettleResultsDto: {
    /**
     * 活动id
     */
    activityId: number;
    /**
     * 活动类型
     */
    type: number;
    /**
     * 业务类型，1：aku  2：vendor  3：openPay
     */
    businessType: number;
    /**
     * 产品类型
     */
    productType: number;
    /**
     * 运费优惠规则
     */
    freightRuleDto: {
      /**
       * 金额门槛
       */
      thresholdAmount: number;
      /**
       * 优惠的邮费
       */
      concessionaryFreight: number;
      /**
       * 是否包邮
       */
      freeFreight: boolean;
    };
    /**
     * 是否能参与活动，有用户维度
     */
    participate: boolean;
    /**
     * 不能参加活动的原因
     */
    failureCause: number;
  };
  /**
   * 商家预计配送时间
   */
  deliveryTime: string;
};

export enum PresellSupportType {
  No,
  Yes,
}

export enum SellStatusType {
  SpotGoods = 1,
  PreSale,
}

export enum ChannelType {
  Iprice = 1,
  Priceza,
  Normal = 1001,
  Ad,
}

export const CreativeId = 381;
